import { defaultCountries, FlagImage, parseCountry } from "react-international-phone";
import { InputAdornment, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useState, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';

// CustomPhoneInput component: A phone input field with country selection.
// This component allows users to input a phone number with a country code.
// It provides a dropdown to select the country and formats the phone number accordingly.
export const CustomPhoneInput = ({ value, onChange, required = false, ...restProps }) => {

    const { t } = useTranslation();

    // Helper function to find a country by its ISO2 code.
    const findCountryByIso2 = (iso2) => defaultCountries?.find(([name, cIso2, dialCode]) => cIso2 === iso2);

    // Helper function to parse the phone number and extract the local number (excluding the country code).
    const getLocalNumber = (phone, dialCode) => {
        const code = `+${dialCode}`;
        if (!phone || phone?.length === code?.length) return "";
        if (phone?.startsWith(code)) {
            return phone?.slice(code?.length);
        }
        return phone;
    };

    // Determine the initial country and local number based on the provided value.
    const initialCountry = useMemo(() => {
        if (!value) return parseCountry(findCountryByIso2("fr")); // Default to France if no value is provided.
        // Match the value to a country based on its dial code.
        for (const c of defaultCountries) {
            const code = `+${c[2]}`;
            if (value?.startsWith(code)) {
                return parseCountry(c);
            }
        }
        return parseCountry(findCountryByIso2("fr")); // Default to France if no match is found.
    }, [value]);

    // State to manage the selected country and the local phone number.
    const [country, setCountry] = useState(initialCountry);
    const [localNumber, setLocalNumber] = useState(getLocalNumber(value, country?.dialCode));

    // Handle changes to the selected country.
    // Clears the input field and notifies the parent component of the change.
    const handleCountryChange = useCallback((event) => {
        const newCountryIso2 = event?.target?.value;
        const newCountryRaw = findCountryByIso2(newCountryIso2);
        if (newCountryRaw) {
            const newCountry = parseCountry(newCountryRaw);
            setCountry(newCountry);
            setLocalNumber("");
            onChange("");
        }
    }, [onChange]);

    // Handle changes to the phone number input.
    // Updates the local number and notifies the parent component with the full phone number.
    const handleInputChange = useCallback((event) => {
        const newLocal = event?.target?.value;
        setLocalNumber(newLocal);
        // Construct the full phone number (country code + local number).
        const finalPhone = newLocal === "" ? "" : `+${country?.dialCode}${newLocal}`;
        onChange(finalPhone);
    }, [country, onChange]);

    // Generate the list of country options for the dropdown menu.
    const countryOptions = useMemo(() => {
        return defaultCountries?.map((defaultCounty) => {
            const parsedCountry = parseCountry(defaultCounty);
            return (
                <MenuItem key={parsedCountry?.iso2} value={parsedCountry?.iso2} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FlagImage iso2={parsedCountry?.iso2} />
                    <Typography>{parsedCountry?.name}</Typography>
                    <Typography color="gray">+{parsedCountry?.dialCode}</Typography>
                </MenuItem>
            );
        });
    }, []);

    return (
        <TextField
            fullWidth
            className="addUser_InputField"
            required={required}
            value={localNumber}
            onChange={handleInputChange}
            type="tel"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Select
                            MenuProps={{
                                style: {
                                    height: "300px",
                                    width: "360px",
                                    top: "10px",
                                    left: "-34px"
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left"
                                }
                            }}
                            sx={{
                                width: "max-content",
                                fieldset: {
                                    display: "none"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "none"
                                },
                                '&.Mui-focused:has(div[aria-expanded="false"])': {
                                    fieldset: {
                                        display: "block"
                                    }
                                },
                                ".MuiSelect-select": {
                                    padding: "8px",
                                    paddingRight: "24px !important"
                                },
                                svg: {
                                    right: 0
                                }
                            }}
                            value={country?.iso2}
                            onChange={handleCountryChange}
                            renderValue={(value) => (
                                <FlagImage iso2={value} />
                            )}
                        >
                            {countryOptions}
                        </Select>
                    </InputAdornment>
                )
            }}
            error={localNumber === "" && required}
            helperText={(localNumber === "" && required) && (t('isRequired'))}
            {...restProps}
        />
    );
};